// Combat visuals: sword swings, bows and staves raised to shoot, shields held up, stuns, floating numbers, puffs.
import { Sprite } from 'pixi.js';
import type { Sprites } from '../assets';
import type { WorldRenderer } from './renderer';

/** A swing's length in seconds (the client holds the swing's facing as long: swingDuration in client/game.ts). */
export const SWING = 0.28;
const NUMBER = 0.8, PUFF = 0.45;
/** How long a bow or staff is held out after a shot, and how far it kicks back (world pixels). */
const AIM = 0.3, RECOIL = 1.5;
const easeOut = (t: number) => 1 - (1 - t) * (1 - t);
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
const clamp01 = (t: number) => Math.min(1, Math.max(0, t));

/** Swing phases as fractions of SWING: draw the blade back, slash through the arc, then recover. */
const WINDUP = 0.22, SLASH = 0.62;
/** How far the blade is drawn back before the slash, and carried past the arc after it (radians). */
const DRAW_BACK = 0.45, OVERSHOOT = 0.2;
/** How far the body steps into the slash (world pixels). */
const LUNGE = 1.5;
/** From the hilt to the blade's tip in a weapon icon (hilt at 3,13, tip near 14,2), in world pixels. */
const BLADE = 14;

interface Swing {
  entity: number; angle: number; arc: number; start: number; sprite: Sprite;
  /** 1 sweeps clockwise on screen, -1 back the other way: swings alternate forehand and backhand. */
  dir: number;
  /** The hand holding the hilt, from the feet (world pixels), fixed for the whole swing. */
  hand: { x: number; y: number };
}
interface Aim { entity: number; angle: number; start: number; sprite: Sprite; hand: { x: number; y: number } }
interface FloatText { x: number; y: number; text: string; color: string; start: number }
interface Puff { x: number; y: number; color: number; start: number }
/** A character holding a shield up (its icon) or stunned, this frame. f: their facing (Facing: down, left, right, up). */
export interface Stance { entity: number; f: number; shield: string; stunned: boolean }
/** Where a raised shield sits from the feet, by facing, and whether it's in front of the body. */
const GUARD: Record<number, { x: number; y: number; front: boolean }> = {
  0: { x: 3, y: -4, front: true }, 1: { x: -4, y: -5, front: true }, 2: { x: 4, y: -5, front: true }, 3: { x: 0, y: -6, front: false },
};

/**
 * Where a character's hand is, from its feet, swinging toward `angle`: the hand on that side of the body (the
 * character templates' hand pixels: generate_textures.py), or the front hand when swinging sideways.
 */
function handFor(angle: number): { x: number; y: number } {
  const dx = Math.cos(angle), dy = Math.sin(angle);
  if (Math.abs(dx) >= Math.abs(dy)) return { x: dx < 0 ? -1 : 1, y: -6 }; // side-on: the hand at the front
  return { x: dx < 0 ? -5 : 4, y: -6 };                                     // facing up or down: the nearer hand
}

export class Effects {
  private swings: Swing[] = [];
  private aims: Aim[] = [];
  private numbers: FloatText[] = [];
  private puffs: Puff[] = [];
  private lastDir = new Map<number, number>();
  private guards = new Map<number, { sprite: Sprite; icon: string }>();
  private stances: Stance[] = [];

  constructor(private readonly renderer: WorldRenderer, private readonly sprites: Sprites) { }

  swing(entity: number, icon: string, angle: number, arcDegrees: number, now: number): void {
    for (const s of this.swings.filter(s => s.entity === entity)) s.sprite.destroy(); // a new swing replaces the last
    this.swings = this.swings.filter(s => s.entity !== entity);
    const sprite = new Sprite(this.sprites.get(icon)[0]);
    sprite.anchor.set(3 / 16, 13 / 16); // weapon icons point up-right from the hilt
    this.renderer.sorted.addChild(sprite);
    const dir = -(this.lastDir.get(entity) ?? -1);
    this.lastDir.set(entity, dir);
    this.swings.push({ entity, angle, arc: arcDegrees * Math.PI / 180, start: now, sprite, dir, hand: handFor(angle) });
  }

  /** A bow or staff held out toward `angle` for a moment after a shot, kicking back as it fires. */
  aim(entity: number, icon: string, angle: number, now: number): void {
    for (const a of this.aims.filter(a => a.entity === entity)) a.sprite.destroy();
    this.aims = this.aims.filter(a => a.entity !== entity);
    const sprite = new Sprite(this.sprites.get(icon)[0]);
    sprite.anchor.set(0.5); // held in the middle: the bow's grip, the staff's shaft
    sprite.rotation = angle + Math.PI / 4; // icons point up-right
    this.renderer.sorted.addChild(sprite);
    this.aims.push({ entity, angle, start: now, sprite, hand: handFor(angle) });
  }

  /**
   * How a swinging character's body moves this frame: a step toward the slash (offset in world pixels) and the walk
   * frame to show while stepping; null when it isn't swinging.
   */
  pose(entity: number, now: number): { dx: number; dy: number; frame: number } | null {
    const s = this.swings.find(s => s.entity === entity);
    if (!s) return null;
    const t = clamp01((now - s.start) / SWING);
    // Lean back a little while drawing the blade, step in on the slash, settle during the recovery.
    const step = t < WINDUP ? -0.4 * (t / WINDUP) : t < SLASH ? -0.4 + 1.4 * easeOut((t - WINDUP) / (SLASH - WINDUP)) : 1 - (t - SLASH) / (1 - SLASH);
    return { dx: Math.cos(s.angle) * LUNGE * step, dy: Math.sin(s.angle) * LUNGE * step * 0.7, frame: t >= WINDUP && t < SLASH + 0.15 ? 1 : 0 };
  }

  /** Who has a shield up or is stunned this frame (drawn by draw). */
  setStances(stances: Stance[]): void {
    this.stances = stances;
    for (const [entity, g] of this.guards)
      if (!stances.some(s => s.entity === entity && s.shield === g.icon && !s.stunned)) { g.sprite.destroy(); this.guards.delete(entity); }
    for (const s of stances) {
      if (!s.shield || s.stunned || this.guards.has(s.entity)) continue;
      const sprite = new Sprite(this.sprites.get(s.shield)[0]);
      sprite.anchor.set(0.5);
      sprite.scale.set(0.75);
      this.renderer.sorted.addChild(sprite);
      this.guards.set(s.entity, { sprite, icon: s.shield });
    }
  }

  number(x: number, y: number, text: string, color: string, now: number): void {
    this.numbers.push({ x, y, text, color, start: now });
  }

  puff(x: number, y: number, color: number, now: number): void {
    this.puffs.push({ x, y, color, start: now });
  }

  clear(): void {
    for (const s of this.swings) s.sprite.destroy();
    this.swings = [];
    for (const a of this.aims) a.sprite.destroy();
    this.aims = [];
    this.lastDir.clear();
    for (const g of this.guards.values()) g.sprite.destroy();
    this.guards.clear();
    this.stances = [];
    this.numbers = [];
    this.puffs = [];
  }

  /** Draws everything for this frame. positionOf maps an entity id to its feet (world pixels). */
  draw(now: number, positionOf: (id: number) => { x: number; y: number } | null): void {
    const r = this.renderer, g = r.barsGraphics, z = r.zoom;
    this.swings = this.swings.filter(s => {
      const feet = positionOf(s.entity);
      if (now - s.start > SWING || !feet) { s.sprite.destroy(); return false; }
      const t = clamp01((now - s.start) / SWING);
      const lean = this.pose(s.entity, now)!;
      // The hilt stays in the hand, which moves with the body's step.
      const hx = feet.x + lean.dx + s.hand.x, hy = feet.y + lean.dy + s.hand.y;
      const from = s.angle - s.dir * s.arc / 2, to = s.angle + s.dir * s.arc / 2;
      let blade: number;
      if (t < WINDUP) blade = from - s.dir * DRAW_BACK * easeOut(t / WINDUP);                 // draw back
      else if (t < SLASH) blade = from - s.dir * DRAW_BACK + (to - from + s.dir * (DRAW_BACK + OVERSHOOT)) * easeOutCubic((t - WINDUP) / (SLASH - WINDUP));
      else blade = to + s.dir * OVERSHOOT * (1 - 0.5 * easeOut((t - SLASH) / (1 - SLASH))); // follow through, ease back
      s.sprite.position.set(hx, hy);
      s.sprite.rotation = blade + Math.PI / 4;
      // Behind the body while the blade is raised away from the camera, in front of it otherwise.
      s.sprite.zIndex = feet.y + lean.dy + (Math.sin(blade) < -0.35 ? -0.5 : 0.5);
      s.sprite.alpha = t < 0.85 ? 1 : 1 - (t - 0.85) / 0.15;

      // Slash trail: a crescent the blade's outer half sweeps, from the slash's start to the blade, fading behind it.
      if (t >= WINDUP) {
        const swept = blade - (from - s.dir * DRAW_BACK * 0.5), fade = t < SLASH ? 1 : 1 - (t - SLASH) / (1 - SLASH);
        const steps = Math.max(8, Math.ceil(Math.abs(swept) * BLADE / 1.5)); // a dot every 1.5 px along the tip
        for (let i = 0; i <= steps; i++) {
          const f = i / steps, a = blade - swept * (1 - f);
          for (const [rr, alpha] of [[BLADE, 0.9], [BLADE - 2, 0.6], [BLADE - 4, 0.3]] as const) {
            const p = r.worldToScreen(hx + Math.cos(a) * rr, hy + Math.sin(a) * rr);
            g.rect(p.x, p.y, z, z).fill({ color: 0xebf0ff, alpha: alpha * (0.15 + 0.85 * f) * fade });
          }
        }
      }
      return true;
    });

    this.aims = this.aims.filter(a => {
      const feet = positionOf(a.entity), t = (now - a.start) / AIM;
      if (t > 1 || !feet) { a.sprite.destroy(); return false; }
      const out = 5 - RECOIL * Math.max(0, 1 - t * 4); // kicks back, then settles
      a.sprite.position.set(feet.x + a.hand.x + Math.cos(a.angle) * out, feet.y + a.hand.y + Math.sin(a.angle) * out);
      a.sprite.zIndex = feet.y + (Math.sin(a.angle) < -0.35 ? -0.5 : 0.5); // behind the body when aiming up
      a.sprite.alpha = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
      return true;
    });

    for (const s of this.stances) {
      const feet = positionOf(s.entity);
      if (!feet) continue;
      const guard = this.guards.get(s.entity);
      if (guard) {
        const at = GUARD[s.f] ?? GUARD[0];
        guard.sprite.position.set(feet.x + at.x, feet.y + at.y);
        guard.sprite.zIndex = feet.y + (at.front ? 0.5 : -0.5);
      }
      if (s.stunned) {
        // Three sparks circling the head (under the name tag).
        for (let i = 0; i < 3; i++) {
          const a = now * 5 + i * Math.PI * 2 / 3;
          const sp = r.worldToScreen(feet.x + Math.cos(a) * 6, feet.y - 16 + Math.sin(a) * 1.5);
          const size = Math.max(1, Math.round(z * 1.5));
          g.rect(sp.x - size / 2, sp.y - size / 2, size, size).fill({ color: 0xffe066, alpha: Math.sin(a) < 0 ? 0.6 : 1 });
        }
      }
    }

    this.puffs = this.puffs.filter(p => {
      const t = (now - p.start) / PUFF;
      if (t > 1) return false;
      for (let i = 0; i < 8; i++) {
        const a = i * Math.PI / 4 + 0.3;
        const sp = r.worldToScreen(p.x + Math.cos(a) * (3 + 10 * t), p.y - 5 + Math.sin(a) * 0.7 * (3 + 10 * t));
        const size = Math.max(1, Math.floor((1 - t) * 3 * z));
        g.rect(sp.x - size / 2, sp.y - size / 2, size, size).fill({ color: p.color, alpha: 1 - t });
      }
      return true;
    });

    this.numbers = this.numbers.filter(n => {
      const t = (now - n.start) / NUMBER;
      if (t > 1) return false;
      const sp = r.worldToScreen(n.x, n.y - 20 - 12 * easeOut(t));
      r.text(n.text, n.color, sp.x, sp.y, t < 0.6 ? 1 : 1 - (t - 0.6) / 0.4);
      return true;
    });
  }
}
